import axios, { AxiosError, AxiosPromise } from "axios";
const jsonHeaders = (additional?: { [x: string]: string }) => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  ...additional,
});

const withUnauthorizedRedirect = async <T>(axiosFn: () => AxiosPromise<T>) => {
  try {
    const res = await axiosFn();

    return res;
  } catch (e: unknown) {
    const status = (e as AxiosError).response?.status;

    if (status && [401, 403].includes(status)) {
      window.location.href = `/auth/login?returnTo=${window.location.href}`;
    }

    throw e;
  }
};

type GetRequest = { url: string } & (
  | {
      pagination?: false;
    }
  | {
      pagination: true;
      page: number;
      page_size: number;
    }
);

export const request = {
  getUnauthenticated: async <T>(url: string): Promise<T> => {
    const { data } = await axios.get<T>(url, {
      headers: jsonHeaders(),
      withCredentials: true,
    });
    return data;
  },
  get: async <T>(request: GetRequest): Promise<T> => {
    const params =
      request.pagination === true
        ? { page: request.page, page_size: request.page_size }
        : undefined;
    const { data } = await withUnauthorizedRedirect(() =>
      axios.get<T>(request.url, {
        headers: jsonHeaders(),
        params,
        withCredentials: true,
      }),
    );
    return data;
  },
  put: async <T>(url: string, body: T | null): Promise<T> => {
    const { data } = await withUnauthorizedRedirect(() => {
      return axios.put<T>(url, body, {
        headers: jsonHeaders(),
        withCredentials: true,
      });
    });
    return data;
  },
  post: async <T>(url: string, body: T): Promise<T> => {
    const { data } = await withUnauthorizedRedirect(() => {
      return axios.post<T>(url, body, {
        headers: jsonHeaders(),
        withCredentials: true,
      });
    });
    return data;
  },
  delete: async <T>(url: string): Promise<T> => {
    console.log(url);
    const { data } = await withUnauthorizedRedirect(() => {
      return axios.delete<T>(url, {
        headers: jsonHeaders(),
        withCredentials: true,
      });
    });
    return data;
  },
  postFormData: async <T>(url: string, body: FormData): Promise<T> => {
    const { data } = await withUnauthorizedRedirect(() => {
      return axios.post<T>(url, body, {
        headers: { Accept: "application/json" },
        withCredentials: true,
      });
    });
    return data;
  },
  download: async <T>(url: string, body: T) => {
    const response = await withUnauthorizedRedirect(() => {
      return axios.post<Blob>(url, body, {
        responseType: "blob",
        withCredentials: true,
      });
    });

    console.log({ response });

    const contentDisposition = response.headers["content-disposition"];
    let filename = "";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    const blob = new Blob([response.data]);
    const urlObj = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlObj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(urlObj);
    a.remove();
  },
};
